import { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Badge,
  Button,
  Alert,
} from 'reactstrap';
import { useSetNexusPluginCredentials } from './useNexusPlugins';
import { PLUGIN_CRED_SCHEMA } from './fixture';

/**
 * Credentials-Editor für ein Nexus-Plugin.
 *
 * Spec § "Per-Tenant Credentials": Backend persistiert AEAD-verschlüsselt
 * via DRIVER_CONFIG_KEY. Klartext nur direkt nach Eingabe sichtbar; ein
 * erneutes Laden zeigt nur Felder die nicht-secret sind oder [redacted].
 *
 * tenant_id leer = globaler Default (`plugin_credentials.tenant_id IS NULL`).
 */
export function PluginCredentialsModal({ plugin, onClose }) {
  const schema = PLUGIN_CRED_SCHEMA[plugin.identifier] ?? [];
  const [tenantId, setTenantId] = useState('');
  const [values, setValues] = useState(() => Object.fromEntries(schema.map((f) => [f.key, ''])));
  const [error, setError] = useState(null);
  const save = useSetNexusPluginCredentials();
  const busy = save.isPending;

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = schema.filter((f) => !values[f.key]?.toString().trim()).map((f) => f.label);
    if (missing.length > 0) {
      setError(`Pflichtfelder fehlen: ${missing.join(', ')}`);
      return;
    }
    try {
      await save.mutateAsync({
        id: plugin.identifier,
        tenant_id: tenantId.trim() || null,
        config: values,
      });
      onClose();
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Speichern fehlgeschlagen.');
    }
  };

  if (schema.length === 0) {
    return (
      <Modal isOpen toggle={onClose}>
        <ModalHeader toggle={onClose}>{plugin.identifier}</ModalHeader>
        <ModalBody>
          <Alert color="warning">
            Kein Credential-Schema für <code>{plugin.identifier}</code> hinterlegt.
            Frontend muss <code>PLUGIN_CRED_SCHEMA</code> in <code>fixture.js</code>
            erweitern oder Nexus liefert das Schema dynamisch.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Schließen</Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen toggle={busy ? undefined : onClose} backdrop="static">
      <Form onSubmit={onSubmit}>
        <ModalHeader toggle={busy ? undefined : onClose}>
          {plugin.identifier} · Credentials
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            Secret-Werte werden AEAD-verschlüsselt persistiert (per
            <code> DRIVER_CONFIG_KEY</code>). Backend liefert sie nie wieder
            im Klartext zurück — bei späterem Bearbeiten ist das Feld leer.
          </Alert>

          <FormGroup>
            <Label>Tenant-ID (leer = globaler Default)</Label>
            <Input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="z.B. 42 (leer = gilt für alle Tenants)"
              disabled={busy}
              className="font-monospace"
            />
          </FormGroup>

          {schema.map((field) => (
            <FormGroup key={field.key}>
              <Label className="d-flex align-items-center justify-content-between mb-1">
                <span>
                  {field.label} <span className="text-danger">*</span>
                  {field.secret && (
                    <Badge color="warning" pill className="ms-2 orbit-code-badge-sm">
                      Secret
                    </Badge>
                  )}
                </span>
                <code className="small text-muted">{field.key}</code>
              </Label>
              <InputGroup size="sm">
                <Input
                  type={field.secret ? 'password' : 'text'}
                  value={values[field.key]}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  placeholder={field.placeholder}
                  disabled={busy}
                  autoComplete={field.secret ? 'new-password' : 'off'}
                />
              </InputGroup>
            </FormGroup>
          ))}

          {error && <Alert color="danger" className="mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={busy}>
            {busy ? 'Speichern…' : 'Credentials speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
